import Link from 'next/link'
import styled from 'styled-components'

import LiveStatusBadge from '@/components/Live/LiveStatusBadge'
import { isLive } from '@/util/liveStreaming'
import { useCreatorLiveStream } from 'src/hooks/live/useCreatorLiveStream'

const PROFILE_POLL_INTERVAL_MS = 60000

/**
 * Live entry point on a public creator profile.
 *
 * Renders for any creator who has live streaming set up — LIVE when they are
 * broadcasting, OFFLINE otherwise — so the profile always tells you whether
 * this is someone who goes live at all.
 *
 * Renders nothing when the creator has no stream, has the feature disabled, has
 * unpublished their stream (all a 404 from the API), or when the viewer is
 * signed out (401), since live playback is members-only.
 */
const ProfileLiveLink = ({ username }) => {
  // A profile is ambient — nobody is waiting on it the way they wait on the
  // watch page — so it checks far less often. The watch page keeps the default.
  const { stream, isLoading, notFound, unauthorized, error } = useCreatorLiveStream(username, {
    pollIntervalMs: PROFILE_POLL_INTERVAL_MS
  })

  if (isLoading || unauthorized || notFound || error || !stream) return null

  const live = isLive(stream)

  return (
    <Link href={`/live/${username}`} passHref>
      <Anchor $live={live}>
        <LiveStatusBadge status={stream.status} />
        <Label className="font-soehne-leight">
          {live ? 'Watch the live stream' : 'Not streaming right now'}
        </Label>
      </Anchor>
    </Link>
  )
}

const Anchor = styled.a`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  margin: 10px auto 4px;
  padding: 8px 12px;
  border: 1px solid ${(props) => (props.$live ? '#c0392b' : '#e5e7eb')};
  border-radius: 8px;
  cursor: pointer;
  text-decoration: none;
  color: inherit;
  transition: border-color 0.15s ease, background 0.15s ease;

  &:hover {
    background: ${(props) => (props.$live ? 'rgba(192, 57, 43, 0.06)' : '#f9fafb')};
    border-color: ${(props) => (props.$live ? '#c0392b' : '#d1d5db')};
  }
`

const Label = styled.span`
  font-size: 13px;
`

export default ProfileLiveLink
